/**
 * Legacy per-customer settings/strings fallback — BACKWARD COMPATIBILITY ONLY.
 *
 * This exists solely to keep the small set of customers that were onboarded
 * before the API-driven config migration (see docs/plans/api-driven-config-plan.md)
 * working while they still rely on bundled `config/{code}/settings.yml` and
 * `config/{code}/strings.yml` overlays.
 *
 * Rules (do not weaken these — they are the whole point of this module):
 *  1. `app_client_code` is ALWAYS sourced from the Hub API — never guessed, never
 *     read from a build-time env var. This module is only consulted AFTER the API
 *     has told us which client code is active.
 *  2. Hub API settings/strings ALWAYS win over anything returned from here — this
 *     is a fallback *below* the API layer, never above it.
 *  3. A client code with no bundled entry here (i.e. every NEW customer going
 *     forward) resolves to nothing — those customers are 100% API-driven, by
 *     design, with no bundled legacy config at all.
 *
 * The bundled data itself is produced at build time by scanning `config/{code}/`
 * via `discoverCustomerCodes()` (see generate-legacy-customer-config-plugin.mjs) —
 * nothing here hardcodes which customers exist.
 */

import { normalizeClientCode } from '@/composables/client-code-styles';

export interface LegacyCustomerConfigEntry {
  settings?: Record<string, unknown>;
  /** Strings in the generated-translations shape: { [key]: { [locale]: string } } */
  strings?: Record<string, Record<string, string>>;
}

let legacyCustomerConfig: Record<string, LegacyCustomerConfigEntry> = Object.create(null);

/** Supply the build-time-generated per-customer legacy config from the host app (Vite root). */
export function configureLegacyCustomerConfig(config: Record<string, LegacyCustomerConfigEntry>) {
  legacyCustomerConfig = Object.assign(Object.create(null), config ?? {});
}

export function getLegacyCustomerConfig() {
  return legacyCustomerConfig;
}

/**
 * Returns the bundled legacy settings for `clientCode`, or `{}` if `clientCode`
 * is falsy/unknown or has no bundled entry (the normal case for new, fully
 * API-driven customers).
 */
export function resolveLegacyCustomerSettings(clientCode: unknown): Record<string, unknown> {
  const normalized = normalizeClientCode(clientCode);
  if (!normalized || !Object.hasOwn(legacyCustomerConfig, normalized)) {
    return {};
  }

  return legacyCustomerConfig[normalized]?.settings ?? {};
}

/**
 * Returns the bundled legacy strings for `clientCode` in generated-translations
 * shape, or `{}` if `clientCode` is falsy/unknown or has no bundled entry.
 */
export function resolveLegacyCustomerStrings(clientCode: unknown): Record<string, Record<string, string>> {
  const normalized = normalizeClientCode(clientCode);
  if (!normalized || !Object.hasOwn(legacyCustomerConfig, normalized)) {
    return {};
  }

  return legacyCustomerConfig[normalized]?.strings ?? {};
}

/** True only for client codes that have a bundled legacy overlay — i.e. pre-migration customers. */
export function hasLegacyCustomerConfig(clientCode: unknown): boolean {
  const normalized = normalizeClientCode(clientCode);
  return !!normalized && Object.hasOwn(legacyCustomerConfig, normalized);
}

export function resetLegacyCustomerConfigState() {
  legacyCustomerConfig = Object.create(null);
}
